import { useCallback, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { acquireSocket, releaseSocket } from '@/shared/api';
import { isCurrentChatEvent, useAiChatStore } from './aiChatStore';
import type { AiSource, AiSuggestion } from '../api/ai';

const STREAM_STALL_MS = 120_000;

export function useAiSocket(active: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const busy = useAiChatStore((s) => s.status !== 'idle');
  const subscribed = active || busy;

  useEffect(() => {
    if (!subscribed) return;
    let cancelled = false;
    const store = useAiChatStore.getState;
    const forCurrent = (chatId?: string) =>
      store().status !== 'idle' && isCurrentChatEvent(store().chatId, chatId);

    const onChatCreated = (p: { chatId: string }) => {
      if (store().status !== 'idle' && !store().chatId) store().setChatId(p.chatId);
    };
    const onAccepted = onChatCreated;
    const onToken = (p: { chatId?: string; delta: string }) => {
      if (forCurrent(p.chatId)) store().appendToken(p.delta);
    };
    const onDone = (p: {
      chatId?: string;
      messageId?: string;
      content?: string;
      sources?: AiSource[] | null;
      suggestions?: AiSuggestion[] | null;
    }) => {
      if (forCurrent(p.chatId)) store().finalizeAssistant(p);
    };
    const onContext = (p: { chatId?: string; level: 'warn' | 'forced'; newChatId?: string }) => {
      if (!isCurrentChatEvent(store().chatId, p.chatId)) return;
      if (p.level === 'forced') {
        store().reset();
        if (p.newChatId) store().setChatId(p.newChatId);
        toast('Чат переполнен — начат новый. История сохранена.');
      } else {
        store().setContext('warn');
      }
    };
    const onError = (p: { chatId?: string; message: string }) => {
      if (!isCurrentChatEvent(store().chatId, p.chatId)) return;
      store().abortStream(p.message);
    };
    const onDisconnect = () => {
      if (store().status !== 'idle') {
        store().abortStream('Соединение с ассистентом потеряно. Повторите вопрос.');
      }
    };

    void acquireSocket().then((socket) => {
      if (cancelled || !socket) return;
      socketRef.current = socket;
      socket.on('ai:chat_created', onChatCreated);
      socket.on('ai:accepted', onAccepted);
      socket.on('ai:token', onToken);
      socket.on('ai:done', onDone);
      socket.on('ai:context', onContext);
      socket.on('ai:error', onError);
      socket.on('disconnect', onDisconnect);
    });

    return () => {
      cancelled = true;
      const socket = socketRef.current;
      if (socket) {
        socket.off('ai:chat_created', onChatCreated);
        socket.off('ai:accepted', onAccepted);
        socket.off('ai:token', onToken);
        socket.off('ai:done', onDone);
        socket.off('ai:context', onContext);
        socket.off('ai:error', onError);
        socket.off('disconnect', onDisconnect);
        socketRef.current = null;
      }
      releaseSocket();
    };
  }, [subscribed]);

  const progress = useAiChatStore((s) => {
    const last = s.messages[s.messages.length - 1];
    return `${s.messages.length}:${last?.content.length ?? 0}`;
  });
  useEffect(() => {
    if (!busy) return;
    const timer = setTimeout(() => {
      useAiChatStore.getState().abortStream('Ассистент не ответил. Попробуйте ещё раз.');
    }, STREAM_STALL_MS);
    return () => clearTimeout(timer);
  }, [busy, progress]);

  const send = useCallback((content: string) => {
    const text = content.trim();
    if (!text) return;
    const socket = socketRef.current;
    const store = useAiChatStore.getState();
    if (store.status !== 'idle') return;
    if (!socket) {
      store.setError('Нет соединения с ассистентом.');
      return;
    }
    store.addUserMessage(text);
    socket.emit('ai:ask', { chatId: store.chatId ?? undefined, content: text });
  }, []);

  return { send };
}
